import BaseController from '../..';
import {
    bpAccountingFilters,
    bpAccountingResponse,
    bpPaymentInput
} from '../../../../models/entities/businessPartner/accounting';
import {
    accountingOversightFilters,
    accountingOversightResponse
} from '../../../../models/entities/businessPartner/accountingOversight';
import {
    accountingPnlFilters,
    accountingPnlSummaryResponse
} from '../../../../models/entities/businessPartner/accountingPnl';

export default class BusinessPartnersAccounting extends BaseController {
    async fetchAccounting(businessPartnerId: string, filters: bpAccountingFilters = {}): Promise<bpAccountingResponse> {
        const params = new URLSearchParams();
        if (filters.range) params.set('range', filters.range);
        if (filters.status) params.set('status', filters.status);
        if (filters.search) params.set('search', filters.search);
        const qs = params.toString();
        const path = `/businessPertner/${businessPartnerId}/accounting${qs ? `?${qs}` : ''}`;
        const response = await this.client.get(path);
        return response;
    }

    async fetchOversight(filters: accountingOversightFilters): Promise<accountingOversightResponse> {
        const params = new URLSearchParams();
        if (filters.currencyMode) params.set('currencyMode', filters.currencyMode);
        if (filters.displayCurrency) params.set('displayCurrency', filters.displayCurrency);
        const qs = params.toString();
        const isAll = !filters.cargoOwnerId || filters.cargoOwnerId === '__ALL__';
        const path = isAll
            ? `/businessPertner/accounting/oversight${qs ? `?${qs}` : ''}`
            : `/businessPertner/${filters.cargoOwnerId}/accounting/oversight${qs ? `?${qs}` : ''}`;
        return await this.client.get(path);
    }

    async fetchPnlSummary(filters: accountingPnlFilters = {}): Promise<accountingPnlSummaryResponse> {
        const params = new URLSearchParams();
        if (filters.range) params.set('range', filters.range);
        if (filters.displayCurrency) params.set('displayCurrency', filters.displayCurrency);
        const qs = params.toString();
        return await this.client.get(`/businessPertner/accounting/pnl-summary${qs ? `?${qs}` : ''}`);
    }

    async addPayment(businessPartnerId: string, payment: bpPaymentInput): Promise<unknown> {
        const response = await this.client.post(
            `/businessPertner/${businessPartnerId}/payments`,
            JSON.stringify(payment)
        );
        return response;
    }

    async fetchShipmentInvoices(
        businessPartnerId: string,
        shipmentId: string
    ): Promise<{ invoices: unknown[]; uninvoicedCount: number }> {
        return await this.client.get(
            `/businessPertner/${businessPartnerId}/accounting/shipments/${shipmentId}/invoices`
        );
    }

    async fetchFinancialDocuments(
        businessPartnerId: string
    ): Promise<{ invoices: Array<Record<string, unknown>> }> {
        return await this.client.get(`/businessPertner/${businessPartnerId}/accounting/financial-documents`);
    }

    async sendInvoicePackage(
        businessPartnerId: string,
        input: {
            shipmentId: string;
            shipmentNumber: string;
            contactEmails: string[];
            invoiceFile: File;
            statementFile: File;
            invoiceAmount?: number;
            invoiceCurrency?: string;
            invoiceId?: string;
        }
    ): Promise<{
        ok: boolean;
        invoiceId?: string;
        invoiceNumber?: string;
        isNew?: boolean;
        attachedDocumentCount: number;
        skippedDocumentCount?: number;
    }> {
        this.client.setBodyType('FORM-DATA');
        const formData = new FormData();
        formData.append('shipmentNumber', input.shipmentNumber);
        formData.append('invoice', input.invoiceFile);
        formData.append('statement', input.statementFile);
        if (input.invoiceAmount != null) formData.append('invoiceAmount', String(input.invoiceAmount));
        if (input.invoiceCurrency) formData.append('invoiceCurrency', input.invoiceCurrency);
        if (input.invoiceId) formData.append('invoiceId', input.invoiceId);
        input.contactEmails.forEach((email) => formData.append('contactsEmails[]', email));
        const response = await this.client.post(
            `/businessPertner/${businessPartnerId}/accounting/shipments/${input.shipmentId}/send-invoice`,
            formData
        );
        return response;
    }
}
